import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useDropdownData } from '../hooks/useDropdownData';
import LocalStorageUtil from '../utils/localStorageUtil';
import { storageKeys } from '../utils/constants';
import { persistor, store } from '../store/store';

interface AppDataContextType {
    isDataLoaded: boolean;
    isLoadingData: boolean;
    dataLoadProgress: number; // 0-100
    loadAppData: () => Promise<void>;
    refreshData: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

export const useAppData = () => {
    const context = useContext(AppDataContext);
    if (!context) {
        throw new Error('useAppData must be used within an AppDataProvider');
    }
    return context;
};

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [dataLoadProgress, setDataLoadProgress] = useState(0);
    const { preloadAllData } = useDropdownData();

    const loadAppData = useCallback(async (): Promise<void> => {
        // Check if we have persisted data from previous session
        const currentPersistedData = localStorage.getItem('persist:root');
        const hasPersistedData = currentPersistedData && currentPersistedData !== '{}';
        
        // If already loaded and currently loading, return early
        if (isDataLoaded && isLoadingData) return;
        
        // If we don't have persisted data, mark as not loaded
        if (isDataLoaded && !hasPersistedData) setIsDataLoaded(false);
        
        // If we have both loaded data and persisted data, skip loading (use cache)
        if (isDataLoaded && hasPersistedData) {
            return;
        }

        setIsLoadingData(true);
        setDataLoadProgress(10);

        try {
            // Pass forceRefresh=false to use cached API data if available
            await preloadAllData(!hasPersistedData);
            setDataLoadProgress(70);

            await persistor.flush();
            persistor.persist();
            setDataLoadProgress(90);

            // Wait for persistence with timeout
            let persistenceAttempts = 0;
            const maxAttempts = 10;
            while (persistenceAttempts < maxAttempts) {
                await new Promise(resolve => setTimeout(resolve, 200));
                const persistedData = localStorage.getItem('persist:root');
                if (persistedData && persistedData !== '{}') break;
                persistenceAttempts++;
                if (persistenceAttempts < maxAttempts) await persistor.flush();
            }

            setIsDataLoaded(true);
            setDataLoadProgress(100);

            // Verify persistence
            const finalPersistedData = localStorage.getItem('persist:root');
            if (!(finalPersistedData && finalPersistedData !== '{}')) {
                console.warn('Redux Persist failed - attempting manual persistence...');
                try {
                    const currentState = store.getState();
                    const manualPersistData = {
                        speciesGroup: JSON.stringify(currentState.speciesGroup),
                        dropdown: JSON.stringify(currentState.dropdown),
                        _persist: JSON.stringify({ version: -1, rehydrated: true })
                    };
                    localStorage.setItem('persist:root', JSON.stringify(manualPersistData));
                } catch (manualError) {
                    console.error('Manual persistence also failed:', manualError);
                }
            }
        } catch (error) {
            console.error('Error in app data loading:', error);
            setIsDataLoaded(true);
            setDataLoadProgress(100);
        } finally {
            setIsLoadingData(false);
            setTimeout(() => setDataLoadProgress(0), 1000);
        }
    }, [preloadAllData, isDataLoaded, isLoadingData]);

    const refreshData = useCallback(async (): Promise<void> => {
        setIsDataLoaded(false);
        await loadAppData();
    }, [loadAppData]);

    useEffect(() => {
        const isAuthenticated = LocalStorageUtil.getItem(storageKeys.isAuthenticated, false);
        if (isAuthenticated && !isDataLoaded && !isLoadingData) {
            loadAppData();
        }
    }, [isDataLoaded, isLoadingData, loadAppData]);

    return (
        <AppDataContext.Provider
            value={{
                isDataLoaded,
                isLoadingData,
                dataLoadProgress,
                loadAppData,
                refreshData,
            }}
        >
            {children}
        </AppDataContext.Provider>
    );
};